import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import {
  getIndiaStates,
  getCitiesByStateCode,
  resolveStateCode,
} from "../utils/indiaStateCity";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
  isValidMobileNumber,
} from "../utils/mobileInput";
import {
  emailInputFieldProps,
  getEmailValidationError,
} from "../utils/emailInput";
import { useMobileExistsCheck } from "../utils/useMobileExistsCheck";
import MobileExistsFeedback from "./MobileExistsFeedback";
import { updateCustomerUser, isErpApiSuccess } from "../erpClient";

const isRealCustomerId = (id) =>
  id != null && id !== "" && !String(id).startsWith("TEMP_");

const normalizeCustomerDate = (dateValue) => {
  const raw = String(dateValue || "").trim();
  if (!raw || raw === "0000-00-00") return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
};

const extractCustomerFromPayload = (data) =>
  data?.customer ??
  data?.user ??
  data?.customer_user ??
  data?.data?.customer ??
  data?.data?.user ??
  (data?.id != null && (data.name != null || data.mobile != null) ? data : null);

const mapApiCustomerToLocal = (user, fallback = {}) => {
  if (!user) return fallback;
  return {
    id: user.id ?? fallback.id,
    mobile: user.mobile ?? fallback.mobile ?? "",
    fullName: user.name || user.fullName || fallback.fullName || "",
    name: user.name || user.fullName || fallback.name || "",
    email: user.email ?? fallback.email ?? "",
    state: user.state ?? fallback.state ?? "",
    city: user.city ?? fallback.city ?? "",
    address: user.address ?? fallback.address ?? "",
    area: user.area || user.Area || user.area_name || fallback.area || "",
    aadharNo: user.aadhar_no ?? user.aadharNo ?? fallback.aadharNo ?? "",
    panNo: user.pan_no ?? user.panNo ?? fallback.panNo ?? "",
    birthDate: user.birth_date ?? user.birthDate ?? fallback.birthDate ?? "",
    maritalStatus:
      user.marital_status ?? user.maritalStatus ?? fallback.maritalStatus ?? "",
    anniversaryDate:
      user.anniversary_date ??
      user.anniversaryDate ??
      fallback.anniversaryDate ??
      "",
    balance: user.users_balance ?? user.balance ?? fallback.balance ?? "0",
    saleBalance:
      user.sale_balance ?? user.saleBalance ?? fallback.saleBalance ?? "0",
    goldBalanceGm:
      user.gold_balance_gm ??
      user.goldBalanceGm ??
      user.gold ??
      fallback.goldBalanceGm ??
      "",
    silverBalanceGm:
      user.silver_balance_gm ??
      user.silverBalanceGm ??
      user.silver ??
      fallback.silverBalanceGm ??
      "",
    companyName:
      user.company_name ?? user.companyName ?? fallback.companyName ?? "",
    companyAddress:
      user.company_address ??
      user.companyAddress ??
      fallback.companyAddress ??
      "",
    gstNumber:
      user.GST_number ||
      user.gst_number ||
      user.gstNumber ||
      fallback.gstNumber ||
      "",
    reference: user.reference ?? fallback.reference ?? "",
  };
};

const customSelectStyles = {
  control: (base) => ({
    ...base,
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#dee2e6",
    },
  }),
  input: (base) => ({
    ...base,
    color: "#495057",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#495057",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--theme-primary)"
      : state.isFocused
        ? "#f8f9fa"
        : "white",
    color: state.isSelected ? "white" : "#212529",
    "&:active": {
      backgroundColor: "var(--theme-primary)",
      color: "white",
    },
  }),
};

const CustomerSelection = ({
  // State props
  customerMobile,
  setCustomerMobile,
  customerMobileSearchText,
  setCustomerMobileSearchText,
  customerName,
  setCustomerName,
  customerNameSearchText,
  setCustomerNameSearchText,
  customerAddress,
  setCustomerAddress,
  customerDetails,
  setCustomerDetails,
  customerCity,
  setCustomerCity,
  selectedCustomerId,
  setSelectedCustomerId,
  customerSaveSuccess,
  setCustomerSaveSuccess,

  // Data props
  users,
  setUsers,
  selectedState,
  setSelectedState,

  // Styling
  buttonClass,
  savingCustomer = false,
  setSavingCustomer = null,
  readOnlyCustomerFields = false,
  hideAddButton = false,
  hideLabel = false,
  hideStateCity = false,
  /** When editing an existing customer, skip "already exists" for their current mobile. */
  excludeMobile = null,
  /** Highlight mobile field (e.g. invalid length on save for new customers). */
  mobileInvalid = false,
  onClearMobileInvalid = null,
}) => {
  // Local state for dropdowns
  const [customerMobileDropdownActive, setCustomerMobileDropdownActive] =
    useState(false);
  const [customerNameDropdownActive, setCustomerNameDropdownActive] =
    useState(false);
  const [customerMobileHighlightedIndex, setCustomerMobileHighlightedIndex] =
    useState(0);
  const [customerNameHighlightedIndex, setCustomerNameHighlightedIndex] =
    useState(0);

  const [showCustomerDetailsModal, setShowCustomerDetailsModal] =
    useState(false);
  const [indiaStates, setIndiaStates] = useState([]);
  const [indiaCities, setIndiaCities] = useState([]);
  const [isSavingCustomerDetails, setIsSavingCustomerDetails] = useState(false);

  const mobileExistsCheck = useMobileExistsCheck(customerMobileSearchText, {
    enabled: !readOnlyCustomerFields,
    // Include TEMP_ draft ids so local draft rows are excluded from the check.
    excludeCustomerId:
      selectedCustomerId != null && selectedCustomerId !== ""
        ? selectedCustomerId
        : null,
    excludeMobile: excludeMobile || null,
    localUsers: users,
  });

  useEffect(() => {
    setIndiaStates(getIndiaStates());
  }, []);

  useEffect(() => {
    if (!selectedState) {
      setIndiaCities([]);
      return;
    }

    let cancelled = false;
    const stateCode = resolveStateCode(selectedState, indiaStates);
    if (!stateCode) {
      setIndiaCities([]);
      return;
    }

    getCitiesByStateCode(stateCode).then((list) => {
      if (!cancelled) setIndiaCities(list);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedState, indiaStates]);

  // Refs
  const customerMobileInputRef = useRef(null);
  const customerNameInputRef = useRef(null);
  const customerMobileDropdownRef = useRef(null);
  const customerNameDropdownRef = useRef(null);
  const customerMobileItemsRef = useRef([]);
  const customerNameItemsRef = useRef([]);

  // Filter functions
  const getFilteredUsersByMobile = (searchText) => {
    if (!searchText || searchText.length < 3) return [];
    const searchLower = searchText.toLowerCase();
    return (Array.isArray(users) ? users : []).filter((item) => {
      const mobile = (item?.mobile ?? "").toString().toLowerCase();
      return mobile.includes(searchLower);
    });
  };

  const getFilteredUsersByName = (searchText) => {
    if (!searchText || searchText.length < 3) return [];
    const searchLower = searchText.toLowerCase();
    return (Array.isArray(users) ? users : []).filter((item) => {
      const name = (item?.fullName ?? item?.name ?? "")
        .toString()
        .toLowerCase();
      return name.includes(searchLower);
    });
  };

  // Selection Handlers
  const handleCustomerSelect = (selectedItem) => {
    // Update all fields
    const mobile = sanitizeMobileInput(selectedItem.mobile);
    setCustomerMobile(mobile);
    setCustomerMobileSearchText(mobile);
    setCustomerName(selectedItem.fullName || selectedItem.name);
    setCustomerNameSearchText(selectedItem.fullName || selectedItem.name);
    setCustomerAddress(selectedItem.address);
    setCustomerCity(selectedItem.city);

    setSelectedCustomerId(selectedItem.id);
    setCustomerSaveSuccess(false);

    // Close dropdowns
    setCustomerMobileDropdownActive(false);
    setCustomerNameDropdownActive(false);

    setCustomerDetails({
      email: selectedItem.email || "",
      birthDate: selectedItem.birthDate || "",
      anniversaryDate: selectedItem.anniversaryDate || "",
      companyName: selectedItem.companyName || "",
      companyAddress: selectedItem.companyAddress || "",
      gstNumber: selectedItem.gstNumber || "",
      aadharNo: selectedItem.aadharNo || "",
      panNo: selectedItem.panNo || "",
      maritalStatus: selectedItem.maritalStatus || "",
      balance: selectedItem.balance || "",
      saleBalance: selectedItem.saleBalance || "",
      goldBalanceGm: selectedItem.goldBalanceGm || "",
      silverBalanceGm: selectedItem.silverBalanceGm || "",
      reference: selectedItem.reference || "",
      state: selectedItem.state || "",
      area: selectedItem.area || "",
    });
  };

  // Mobile Input Handlers
  const handleCustomerMobileChange = (e) => {
    const value = sanitizeMobileInput(e.target.value);
    setCustomerMobileSearchText(value);
    setCustomerMobile(value);
    setCustomerMobileDropdownActive(true);
    setCustomerMobileHighlightedIndex(0);
    setSelectedCustomerId(null);
    setCustomerSaveSuccess(false);
    if (typeof onClearMobileInvalid === "function") {
      onClearMobileInvalid();
    }
  };

  useEffect(() => {
    if (!mobileInvalid) return;
    const el = customerMobileInputRef.current;
    if (el) {
      el.focus();
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [mobileInvalid]);

  const handleCustomerMobileKeyDown = (e) => {
    const filtered = getFilteredUsersByMobile(customerMobileSearchText);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setCustomerMobileHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerMobileHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && customerMobileDropdownActive) {
          handleCustomerSelect(filtered[customerMobileHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setCustomerMobileDropdownActive(false);
        break;
      case "Tab":
        setCustomerMobileDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Name Input Handlers
  const handleCustomerNameChange = (e) => {
    const value = e.target.value;
    setCustomerNameSearchText(value);
    setCustomerName(value);
    setCustomerNameDropdownActive(true);
    setCustomerNameHighlightedIndex(0);
    setSelectedCustomerId(null);
    setCustomerSaveSuccess(false);
  };

  const handleCustomerNameKeyDown = (e) => {
    const filtered = getFilteredUsersByName(customerNameSearchText);
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setCustomerNameHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerNameHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && customerNameDropdownActive) {
          handleCustomerSelect(filtered[customerNameHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setCustomerNameDropdownActive(false);
        break;
      case "Tab":
        setCustomerNameDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Click Outside Handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        customerMobileDropdownRef.current &&
        !customerMobileDropdownRef.current.contains(event.target) &&
        customerMobileInputRef.current &&
        !customerMobileInputRef.current.contains(event.target)
      ) {
        setCustomerMobileDropdownActive(false);
      }
      if (
        customerNameDropdownRef.current &&
        !customerNameDropdownRef.current.contains(event.target) &&
        customerNameInputRef.current &&
        !customerNameInputRef.current.contains(event.target)
      ) {
        setCustomerNameDropdownActive(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Dropdown Renders
  const renderCustomerMobileDropdown = () => {
    if (!customerMobileDropdownActive) return null;
    const filtered = getFilteredUsersByMobile(customerMobileSearchText);
    customerMobileItemsRef.current = [];

    const inputRect = customerMobileInputRef.current?.getBoundingClientRect();
    if (!inputRect) return null;

    const portalElement =
      document.getElementById("dropdown-portal") || document.body;

    return ReactDOM.createPortal(
      <div
        ref={customerMobileDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "fixed",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputRect.width}px`,
          minWidth: `${inputRect.width}px`,
          maxHeight: "200px",
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
          fontSize: "12px",
          lineHeight: "1.2",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const isSelected =
              item.mobile.toLowerCase() ===
              customerMobileSearchText.toLowerCase();
            return (
              <div
                key={idx}
                ref={(el) => (customerMobileItemsRef.current[idx] = el)}
                className={`dropdown-item ${idx === customerMobileHighlightedIndex ? "active" : ""
                  }`}
                title={`${item.mobile} - ${item.name}`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === customerMobileHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                  color:
                    idx === customerMobileHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                onMouseEnter={() => setCustomerMobileHighlightedIndex(idx)}
                onMouseDown={() => handleCustomerSelect(item)}
              >
                {item?.mobile ?? ""} - {item?.name ?? item?.fullName ?? ""}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching users</div>
        )}
      </div>,
      portalElement
    );
  };

  const renderCustomerNameDropdown = () => {
    if (!customerNameDropdownActive) return null;
    const filtered = getFilteredUsersByName(customerNameSearchText);
    customerNameItemsRef.current = [];

    const inputRect = customerNameInputRef.current?.getBoundingClientRect();
    if (!inputRect) return null;

    const portalElement =
      document.getElementById("dropdown-portal") || document.body;

    return ReactDOM.createPortal(
      <div
        ref={customerNameDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "fixed",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputRect.width}px`,
          minWidth: `${inputRect.width}px`,
          maxHeight: "200px",
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
          fontSize: "12px",
          lineHeight: "1.2",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const itemName = (item?.fullName ?? item?.name ?? "")
              .toString()
              .toLowerCase();
            const isSelected =
              itemName === customerNameSearchText.toLowerCase();
            return (
              <div
                key={idx}
                ref={(el) => (customerNameItemsRef.current[idx] = el)}
                className={`dropdown-item ${idx === customerNameHighlightedIndex ? "active" : ""
                  }`}
                title={`${item?.name ?? item?.fullName ?? ""} - ${item?.mobile ?? ""
                  }`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === customerNameHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                  color:
                    idx === customerNameHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                onMouseEnter={() => setCustomerNameHighlightedIndex(idx)}
                onMouseDown={() => handleCustomerSelect(item)}
              >
                {item?.name ?? item?.fullName ?? ""} - {item?.mobile ?? ""}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching users</div>
        )}
      </div>,
      portalElement
    );
  };

  return (
    <div className="row mb-1 gy-3">
      {/* Customer Mobile */}
      <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
        <div className="d-flex align-items-center">
          {!hideLabel && (
            <label
              className="form-label me-2 mb-0"
              style={{ width: "80px", fontSize: "0.85rem", fontWeight: "600" }}
            >
              Customer: 
            </label>
          )}
          <div className="position-relative flex-grow-1">
            <input
              ref={customerMobileInputRef}
              {...mobileInputFieldProps}
              value={customerMobileSearchText}
              onChange={readOnlyCustomerFields ? undefined : handleCustomerMobileChange}
              onKeyDown={readOnlyCustomerFields ? undefined : handleCustomerMobileKeyDown}
              onClick={
                readOnlyCustomerFields ? undefined : () => setCustomerMobileDropdownActive(true)
              }
              className={`form-control form-control-sm ${buttonClass} ${
                mobileExistsCheck.exists || mobileInvalid ? "is-invalid" : ""
              }`}
              style={{
                fontSize: "0.85rem",
                backgroundColor: "transparent",
                ...(mobileInvalid
                  ? { borderColor: "#dc3545", boxShadow: "0 0 0 0.2rem rgba(220, 53, 69, 0.25)" }
                  : {}),
              }}
              onFocus={() => {
                if (readOnlyCustomerFields) return;
                if (customerMobileInputRef.current)
                  customerMobileInputRef.current.style.backgroundColor = "#fff";
                setCustomerMobileDropdownActive(true);
              }}
              onBlur={(e) => {
                e.target.style.backgroundColor = "transparent";
              }}
              autoComplete="off"
              placeholder="Enter mobile number"
              readOnly={readOnlyCustomerFields}
              aria-invalid={mobileInvalid || mobileExistsCheck.exists}
            />
            {renderCustomerMobileDropdown()}
            <MobileExistsFeedback
              exists={mobileExistsCheck.exists}
              checking={mobileExistsCheck.checking}
            />
          </div>
        </div>
      </div>

      {/* Customer Name */}
      <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
        <div className="d-flex align-items-center">
          <div className="position-relative flex-grow-1">
            <div className="d-flex align-items-center">
              <input
                ref={customerNameInputRef}
                type="text"
                value={customerNameSearchText}
                onChange={readOnlyCustomerFields ? undefined : handleCustomerNameChange}
                onKeyDown={readOnlyCustomerFields ? undefined : handleCustomerNameKeyDown}
                onClick={
                  readOnlyCustomerFields ? undefined : () => setCustomerNameDropdownActive(true)
                }
                className={`form-control form-control-sm ${buttonClass}`}
                style={{ fontSize: "0.85rem", backgroundColor: "transparent" }}
                onFocus={() => {
                  if (readOnlyCustomerFields) return;
                  if (customerNameInputRef.current)
                    customerNameInputRef.current.style.backgroundColor = "#fff";
                  setCustomerNameDropdownActive(true);
                }}
                onBlur={(e) => {
                  e.target.style.backgroundColor = "transparent";
                }}
                autoComplete="off"
                placeholder="Enter customer name"
                readOnly={readOnlyCustomerFields}
              />
              {customerSaveSuccess && (
                <i
                  className="bi bi-check-circle-fill text-success ms-2"
                  style={{ fontSize: "1.2rem" }}
                ></i>
              )}
            </div>
          </div>
          {renderCustomerNameDropdown()}
        </div>
      </div>

      {/* Address */}
      <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
        <div className="d-flex align-items-center">
          <div className="position-relative flex-grow-1">
            <input
              type="text"
              value={customerAddress || ""}
              onChange={
                readOnlyCustomerFields ? undefined : (e) => setCustomerAddress(e.target.value)
              }
              placeholder="Enter address"
              className={`form-control form-control-sm ${buttonClass}`}
              style={{
                fontSize: "0.85rem",
                backgroundColor: "transparent",
                cursor: "text",
              }}
              title={customerAddress || ""}
              readOnly={readOnlyCustomerFields}
            />
          </div>
        </div>
      </div>

      {/* Area with State-City Display */}
      <div className="col-lg-2 col-md-4 col-sm-6 mb-2">
        <div className="d-flex align-items-center">
          <div className="position-relative flex-grow-1">
            {!hideStateCity && (
            <div
              className="text-black fst-italic small"
              style={{
                position: "absolute",
                top: "-20px",
                left: "0",
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              {(() => {
                const isNewCustomer = !selectedCustomerId;
                const displayState =
                  customerDetails.state || (isNewCustomer ? "Gujarat" : "");
                const displayCity =
                  customerCity || (isNewCustomer ? "Ahmedabad" : "");
                if (!displayState && !displayCity) return null;
                return `${displayState} - ${displayCity}`;
              })()}
            </div>
            )}
            <input
              type="text"
              value={customerDetails.area || ""}
              onChange={
                readOnlyCustomerFields
                  ? undefined
                  : (e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      area: e.target.value,
                    })
              }
              placeholder="Enter area"
              className={`form-control form-control-sm ${buttonClass}`}
              style={{
                fontSize: "0.85rem",
                backgroundColor: "transparent",
                cursor: "text",
              }}
              readOnly={readOnlyCustomerFields}
            />
          </div>
        </div>
      </div>

      {/* Add Button */}
      {!hideAddButton && (
        <div className="col-auto mb-2">
          <button
            className={`btn btn-sm btn-success ${buttonClass}`}
            onClick={() => {
              setShowCustomerDetailsModal(true);
              if (customerDetails.state) {
                const matchedState = indiaStates.find(
                  (s) => s.name === customerDetails.state
                );
                if (matchedState) {
                  setSelectedState(matchedState.iso2);
                } else {
                  setSelectedState("");
                }
              } else {
                const gujarat = indiaStates.find((s) => s.name === "Gujarat");
                if (gujarat) {
                  setSelectedState(gujarat.iso2);
                  setCustomerDetails((prev) => ({
                    ...prev,
                    state: gujarat.name,
                  }));
                  setCustomerCity("Ahmedabad");
                }
              }
            }}
            style={{
              fontSize: "0.85rem",
              padding: "0.25rem 0.5rem",
              height: "31px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Icon icon="solar:add-circle-bold" width="16" height="16" />
          </button>
        </div>
      )}

      {/* Customer Details Modal */}
      <Modal
        show={showCustomerDetailsModal}
        onHide={() => setShowCustomerDetailsModal(false)}
        centered
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Customer Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Row 1: Customer Name, Mobile Number, Email */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label>Customer Name</Form.Label>
                <Form.Control
                  type="text"
                  value={customerNameSearchText}
                  onChange={(e) => setCustomerNameSearchText(e.target.value)}
                  placeholder="Enter customer name"
                />
              </Col>
              <Col md={4}>
                <Form.Label>Mobile Number</Form.Label>
                <Form.Control
                  {...mobileInputFieldProps}
                  value={customerMobileSearchText}
                  onChange={(e) => {
                    const value = sanitizeMobileInput(e.target.value);
                    setCustomerMobileSearchText(value);
                    setCustomerMobile(value);
                    if (typeof onClearMobileInvalid === "function") {
                      onClearMobileInvalid();
                    }
                  }}
                  placeholder="Enter mobile number"
                  isInvalid={mobileExistsCheck.exists || mobileInvalid}
                />
                <MobileExistsFeedback
                  exists={mobileExistsCheck.exists}
                  checking={mobileExistsCheck.checking}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  {...emailInputFieldProps}
                  value={customerDetails.email}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      email: e.target.value,
                    })
                  }
                  placeholder="Enter email"
                />
              </Col>
            </Row>

            {/* Row 2: State, City, Address */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label>State</Form.Label>
                <Select
                  options={indiaStates.map((state) => ({
                    label: state.name,
                    value: state.iso2,
                  }))}
                  value={
                    customerDetails.state
                      ? {
                        label: customerDetails.state,
                        value:
                          indiaStates.find((s) => s.name === customerDetails.state)
                            ?.iso2 || "",
                      }
                      : null
                  }
                  onChange={(selectedOption) => {
                    setCustomerDetails({
                      ...customerDetails,
                      state: selectedOption ? selectedOption.label : "",
                    });
                    setSelectedState(
                      selectedOption ? selectedOption.value : ""
                    );
                    setCustomerCity("");
                  }}
                  styles={customSelectStyles}
                  placeholder="Select State"
                  isClearable
                />
              </Col>
              <Col md={4}>
                <Form.Label>City</Form.Label>
                <Select
                  options={indiaCities.map((city) => ({
                    label: city.name,
                    value: city.name,
                  }))}
                  value={
                    customerCity
                      ? { label: customerCity, value: customerCity }
                      : null
                  }
                  onChange={(selectedOption) => {
                    setCustomerCity(selectedOption ? selectedOption.value : "");
                  }}
                  styles={customSelectStyles}
                  placeholder="Select City"
                  isClearable
                />
              </Col>
              <Col md={4}>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder="Enter address"
                />
              </Col>
            </Row>

            {/* Row 3: Area, Aadhar No, PAN No */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label>Area</Form.Label>
                <Form.Control
                  type="text"
                  value={customerDetails.area}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      area: e.target.value,
                    })
                  }
                  placeholder="Enter area"
                />
              </Col>
              <Col md={4}>
                <Form.Label>Aadhar No</Form.Label>
                <Form.Control
                  type="text"
                  value={customerDetails.aadharNo}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      aadharNo: e.target.value,
                    })
                  }
                  placeholder="Enter Aadhar No"
                />
              </Col>
              <Col md={4}>
                <Form.Label>PAN No</Form.Label>
                <Form.Control
                  type="text"
                  value={customerDetails.panNo}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      panNo: e.target.value,
                    })
                  }
                  placeholder="Enter PAN No"
                />
              </Col>
            </Row>

            {/* Row 4: Birth Date, Marital Status, Anniversary Date */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label>Birth Date</Form.Label>
                <Form.Control
                  type="date"
                  value={customerDetails.birthDate}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      birthDate: e.target.value,
                    })
                  }
                />
              </Col>
              <Col md={4}>
                <Form.Label>Marital Status</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    label="Married"
                    name="maritalStatus"
                    value="married"
                    checked={customerDetails.maritalStatus === "married"}
                    onChange={(e) =>
                      setCustomerDetails({
                        ...customerDetails,
                        maritalStatus: e.target.value,
                      })
                    }
                  />
                  <Form.Check
                    type="radio"
                    label="Unmarried"
                    name="maritalStatus"
                    value="unmarried"
                    checked={customerDetails.maritalStatus === "unmarried"}
                    onChange={(e) =>
                      setCustomerDetails({
                        ...customerDetails,
                        maritalStatus: e.target.value,
                      })
                    }
                  />
                </div>
              </Col>
              <Col md={4}>
                <Form.Label>Anniversary Date</Form.Label>
                <Form.Control
                  type="date"
                  value={customerDetails.anniversaryDate}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      anniversaryDate: e.target.value,
                    })
                  }
                />
              </Col>
            </Row>

            {/* Row 5: Balance, Company Name, Company Address */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label>Balance</Form.Label>
                <Form.Control
                  type="number"
                  value={customerDetails.balance}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      balance: e.target.value,
                    })
                  }
                  placeholder="Enter Balance"
                />
              </Col>
              <Col md={4}>
                <Form.Label>Company Name</Form.Label>
                <Form.Control
                  type="text"
                  value={customerDetails.companyName}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      companyName: e.target.value,
                    })
                  }
                  placeholder="Enter Company Name"
                />
              </Col>
              <Col md={4}>
                <Form.Label>Company Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={customerDetails.companyAddress}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      companyAddress: e.target.value,
                    })
                  }
                  placeholder="Enter Company Address"
                />
              </Col>
            </Row>

            {/* Row 6: GST Number, Reference */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label>GST Number</Form.Label>
                <Form.Control
                  type="text"
                  value={customerDetails.gstNumber}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      gstNumber: e.target.value,
                    })
                  }
                  placeholder="Enter GST Number"
                />
              </Col>
              <Col md={4}>
                <Form.Label>Reference</Form.Label>
                <Form.Control
                  type="text"
                  value={customerDetails.reference}
                  onChange={(e) =>
                    setCustomerDetails({
                      ...customerDetails,
                      reference: e.target.value,
                    })
                  }
                  placeholder="Enter Reference"
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            disabled={savingCustomer || isSavingCustomerDetails}
            onClick={async () => {
              if (!customerMobileSearchText || !customerNameSearchText) {
                toast.error("Mobile number and name are required");
                return;
              }

              if (!isValidMobileNumber(customerMobileSearchText)) {
                toast.error("Enter valid number");
                return;
              }

              const emailError = getEmailValidationError(
                customerDetails.email,
              );
              if (emailError) {
                toast.error(emailError);
                return;
              }

              if (mobileExistsCheck.exists) {
                toast.error("Mobile number already exists");
                return;
              }

              const applyLocalCustomer = (customerData, { markSaveSuccess }) => {
                setUsers((prev) => {
                  const list = Array.isArray(prev) ? prev : [];
                  const index = list.findIndex(
                    (u) =>
                      String(u.id) === String(customerData.id) ||
                      String(u.mobile || "").trim() ===
                        String(customerData.mobile || "").trim(),
                  );
                  if (index >= 0) {
                    const updated = [...list];
                    updated[index] = { ...updated[index], ...customerData };
                    return updated;
                  }
                  return [...list, customerData];
                });

                setSelectedCustomerId(customerData.id);
                setCustomerMobile(customerData.mobile);
                setCustomerMobileSearchText(customerData.mobile);
                setCustomerName(customerData.fullName || customerData.name);
                setCustomerNameSearchText(
                  customerData.fullName || customerData.name,
                );
                setCustomerAddress(customerData.address || "");
                setCustomerCity(customerData.city || "");
                setCustomerDetails((prev) => ({
                  ...prev,
                  email: customerData.email || "",
                  state: customerData.state || "",
                  area: customerData.area || "",
                  aadharNo: customerData.aadharNo || "",
                  panNo: customerData.panNo || "",
                  birthDate: customerData.birthDate || "",
                  maritalStatus: customerData.maritalStatus || "",
                  anniversaryDate: customerData.anniversaryDate || "",
                  balance: customerData.balance || prev.balance || "0",
                  saleBalance:
                    customerData.saleBalance || prev.saleBalance || "0",
                  goldBalanceGm:
                    customerData.goldBalanceGm || prev.goldBalanceGm || "",
                  silverBalanceGm:
                    customerData.silverBalanceGm || prev.silverBalanceGm || "",
                  companyName: customerData.companyName || "",
                  companyAddress: customerData.companyAddress || "",
                  gstNumber: customerData.gstNumber || "",
                  reference: customerData.reference || "",
                }));
                setCustomerSaveSuccess(Boolean(markSaveSuccess));
                setShowCustomerDetailsModal(false);
              };

              const draftCustomerData = {
                id: selectedCustomerId || `TEMP_${Date.now()}`,
                mobile: customerMobileSearchText,
                fullName: customerNameSearchText,
                name: customerNameSearchText,
                email: customerDetails.email || "",
                state: customerDetails.state || "",
                city: customerCity || "",
                address: customerAddress || "",
                area: customerDetails.area || "",
                aadharNo: customerDetails.aadharNo || "",
                panNo: customerDetails.panNo || "",
                birthDate: customerDetails.birthDate || "",
                maritalStatus: customerDetails.maritalStatus || "",
                anniversaryDate: customerDetails.anniversaryDate || "",
                balance: customerDetails.balance || "0",
                saleBalance: customerDetails.saleBalance || "0",
                companyName: customerDetails.companyName || "",
                companyAddress: customerDetails.companyAddress || "",
                gstNumber: customerDetails.gstNumber || "",
                reference: customerDetails.reference || "",
              };

              // Existing ERP customer → persist via update API immediately
              if (isRealCustomerId(selectedCustomerId)) {
                setIsSavingCustomerDetails(true);
                if (typeof setSavingCustomer === "function") {
                  setSavingCustomer(true);
                }
                try {
                  const response = await updateCustomerUser({
                    id: selectedCustomerId,
                    name: customerNameSearchText.trim() || undefined,
                    mobile: customerMobileSearchText.trim() || undefined,
                    email: customerDetails.email || undefined,
                    state: customerDetails.state || undefined,
                    city: customerCity || undefined,
                    address: customerAddress || undefined,
                    area: customerDetails.area || undefined,
                    aadhar_no: customerDetails.aadharNo || undefined,
                    pan_no: customerDetails.panNo || undefined,
                    birth_date: normalizeCustomerDate(
                      customerDetails.birthDate,
                    ),
                    marital_status: customerDetails.maritalStatus || undefined,
                    anniversary_date: normalizeCustomerDate(
                      customerDetails.anniversaryDate,
                    ),
                    company_name: customerDetails.companyName || undefined,
                    company_address:
                      customerDetails.companyAddress || undefined,
                    gst_number: customerDetails.gstNumber || undefined,
                    reference: customerDetails.reference
                      ? String(customerDetails.reference)
                      : undefined,
                  });

                  if (!isErpApiSuccess(response)) {
                    throw new Error(
                      response?.message || "Failed to update customer",
                    );
                  }

                  const apiCustomer = extractCustomerFromPayload(
                    response?.data ?? response,
                  );
                  const updatedCustomer = mapApiCustomerToLocal(
                    apiCustomer,
                    draftCustomerData,
                  );

                  applyLocalCustomer(updatedCustomer, {
                    // Already persisted; do not re-send via sale new_user
                    markSaveSuccess: false,
                  });
                  toast.success(
                    response?.message &&
                      String(response.message).toLowerCase() !== "radha"
                      ? response.message
                      : "Customer updated successfully",
                  );
                } catch (error) {
                  console.error("Error updating customer:", error);
                  toast.error(
                    error?.response?.data?.message ||
                      error?.message ||
                      "Failed to update customer",
                  );
                } finally {
                  setIsSavingCustomerDetails(false);
                  if (typeof setSavingCustomer === "function") {
                    setSavingCustomer(false);
                  }
                }
                return;
              }

              // New / draft customer → keep local draft until bill save
              applyLocalCustomer(draftCustomerData, { markSaveSuccess: true });
              toast.success("Customer details saved locally");
            }}
          >
            {savingCustomer || isSavingCustomerDetails ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CustomerSelection;
